import { inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { LoggingService } from './logging.service';
import { APIService } from './api.service';
import { ConfigurationService } from '../pages/offline/configuration.service';
import { from, Observable } from 'rxjs';
import * as Sentry from '@sentry/angular-ivy';
import { AmplifyApiService } from './amplify-api.service';
export interface Permission {
  code: string;
  description: string;
}

export interface Entity {
  code: string;
  description: string;
  permissions: Array<Permission>;
}

export const Entities = {
  Permissions: 'permissions',
  Users: 'users_ui',
  User: 'user_ui',
  Schedules: 'schedules',
  Event: 'event',
  Task: 'task_ui',
  Messages: 'messages_ui',
  Offline: 'offline_ui',
  Map: 'map',
  Maps: 'maps',
  Forms: 'forms',
  Form: 'form',
  MediaBundles: 'media-bundles',
  MediaBundle: 'media-bundle',
  NewsList: 'news-list',
  FormBuilder: 'form-builder',
  InvestmentProjects: 'investment-projects',
  FormDefinition: 'form-definition_ui',
  DomainValues: 'domain-values_ui',
  DomainValue: 'domain-value_ui',
  Entity: 'entity',
  News: 'news',
  Program: 'program_ui',
  Programs: 'programs_ui',
  Dashboards: 'dashboards',
  Contacts: 'contacts-ui',
  Contact: 'contact-ui',
  ReportsList: 'reports-list_ui',
  Accounting: 'accounting',
  Invoices: 'invoices_ui',
  Invoice: 'invoice_ui',
  Report: 'report_ui',
  ReportPlantSpecies: 'report_plant_species',
  ReportPlantSpeciesCommercialData: 'report_plant_species_commercial_data',
  ReportPolygonCharacteristics: 'report_polygon_characteristics_report',
  ReportFloraProjectArea: 'report_flora_project_area',
  ReportBioFertilizer: 'report_biofertilizer',
  ReportBioFertilizerCommercialData: 'report_biofertilizer_commercial_data',
  ReportMaterialEquipment: 'report_materials_equipment',
  ReportInvSeedlingNursery: 'report_inventory_seedlings_nurseries',
  ReportGermplasmCatalog: 'report_germplasm_catalog',
  ReportSilviculturalMonitoring: 'report_silvicultural_monitoring_plot',
  ReportBiodiversityMonitoring: 'report_monitoring_biodiversity_plot',
  ReportForms: 'report_forms',
  ReportKPI: 'report_kpi',
  ReportProductiveModel: 'report_productive_model',
  ReportFarmEdaphic: 'report_farm_edaphic_characteristics',
  ReportInvestmentProjectArea: 'report_investment_project_area',
  ReportSpeciesPlot: 'report_species_plot',
  Properties: 'properties_ui',
  Property: 'property_ui',
  AnnualPlan: 'annual-plan',
  ProductiveModel: 'form-productive-model',
  Contract: 'form-contract',
  MandateContract: 'form-mandate-contract',
  FarmAgriculturalActivities: 'form-farm-agricultural-activities',
  FarmEdaphicCharacteristics: 'form-farm-edaphic-characteristics',
  DisbursementRequest: 'form-disbursement-request',
  PreContract: 'form-pre-contract',
  AccountRequests: 'account-requests-ui',
  AccountRequest: 'account-request-ui',
  ProjectNotifications: 'project-notifications_ui',
  ProjectNotification: 'project-notification_ui',
  BusinessIntelligences: 'business-intelligences_ui',
} as const;

export const Permissions = {
  Create: 'CREATE',
  CreateFromDraft: 'CREATE_FROM_DRAFT',
  Read: 'READ',
  Update: 'UPDATE',
  Delete: 'DELETE',
  Copy: 'COPY',
  EnableDisable: 'ENABLE-DISABLE',
  ResetPassword: 'RESET-PASSWORD',
  ChangePassword: 'CHANGE-PASSWORD',
  Publish: 'PUBLISH',
  Archive: 'ARCHIVE',
  Intersect: 'INTERSECT',
  CanChangeStatusOne: 'CHANGE_STATUS_ONE',
  CanChangeStatusTwo: 'CHANGE_STATUS_TWO',
  CanChangeStatusThree: 'CHANGE_STATUS_THREE',
  CanChangeStatusFour: 'CHANGE_STATUS_FOUR',
  CanChangeStatusFive: 'CHANGE_STATUS_FIVE',
  CanChangeStatusSix: 'CHANGE_STATUS_SIX',
  CanChangeStatusSeven: 'CHANGE_STATUS_SEVEN',
  CanChangeStatusEight: 'CHANGE_STATUS_EIGHT',
  CanChangeStatusNine: 'CHANGE_STATUS_NINE',
  CanChangeStatusTen: 'CHANGE_STATUS_TEN',
  Print: 'GENERATE_PDF',
  SendToOwner: 'SEND_FORM_TO_OWNER',
  Documents: 'DOCUMENTS',
  InvoiceFullUpdate: 'INVOICE_FULL_UPDATE',
  Complete: 'COMPLETE',
  Approve: 'APPROVE',
  Transfer: 'TRANSFER',
  CanUseOtherTask: 'CAN_USE_OTHER_TASK',
  ChangeProgram: 'CHANGE_PROGRAM',
  UpdateRole: 'UPDATE_ROLE',
  UpdateProgram: 'UPDATE_PROGRAM',
} as const;

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private entities: Array<Entity> = [];

  constructor(
    private readonly logger: LoggingService,
    private readonly apiService: APIService,
    private readonly configurationService: ConfigurationService,
  ) {}

  public async getPermissionsFor(entity: string): Promise<Array<Permission>> {
    if (!this.entities.length) {
      await this.loadPermissions();
    }

    return this.entities.filter((x) => x.code === entity)[0]?.permissions;
  }

  public async userCan(permission: string, entity: string): Promise<boolean> {
    if (!this.entities.length) {
      await this.loadPermissions();
    }

    return !!this.entities.filter((x) => x.code === entity)[0]?.permissions.find((y) => y.code === permission);
  }

  public userCanDo(permission: string, entity: string): Observable<boolean> {
    return from(this.userCan(permission, entity));
  }

  public async updatePermissionsForRole(role: string, entity: unknown): Promise<void> {
    if (!role || !entity) {
      throw new Error('role and entity are mandatory');
    }

    this.logger.debug('updatePermissionsForRole', role, entity);
    return this.API.patch(environment.API_NAME, `/v2/permissions/${role}`, { body: entity });
  }

  private async loadPermissions(): Promise<void> {
    try {
      if (this.apiService.onLine) {
        this.entities = await this.API.get(environment.API_NAME, `/v2/permissions`, {});
        this.configurationService.populateEntities(this.entities);
        this.logger.info('loaded permissions online');
      } else {
        this.entities = await this.configurationService.getEntities();
        this.logger.info('loaded permissions offline');
      }
      const shortEntities = [];
      for (const entity of this.entities) {
        const perms = this.getShortPermissions(entity.permissions);
        if (perms.length) {
          shortEntities.push(
            JSON.stringify({
              cd: entity.code,
              perms: this.getShortPermissions(entity.permissions),
            }),
          );
        }
      }
      Sentry.setContext('permissions', { entities: shortEntities });
    } catch (error) {
      this.logger.error(error);
    }
  }

  private getShortPermissions(permissions: Array<any>): Array<any> {
    if (!permissions || permissions.length === 0) {
      return [];
    }
    const shortPermissions = [];
    for (const permission of permissions) {
      shortPermissions.push(permission.code);
    }
    return shortPermissions;
  }

  public getEntities(): Array<Entity> {
    return this.entities;
  }

  private readonly API = inject(AmplifyApiService);
}
